import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  createAIReviewJob,
  approveProtocolIntelligence,
  createAIReviewJobGroup,
  uploadProtocolIntelligence,
  deleteAIReviewJobGroup,
  exportAIReviewJobs,
  downloadAIReviewJobResults,
  deleteAIReviewJob,
  retryAIReviewJob,
  addJobToGroup,
  removeJobFromGroup,
  getAIReviewJobDetails,
  getAIReviewJobGroups,
  getAIReviewSourceType,
  getProtocolIntelligenceDetails,
  submitAIReviewJob,
  updateAIReviewJob,
  updateAIReviewJobGroup,
  getAIReviewJobResult,
  getAIReviewJobResultForExport,
  getAIReviewJobResultIssueCodeList,
  getAIReviewJobResultDocsList,
  getAIReviewJobResultRelevancyList,
  getAIReviewRelevancyDocSummary,
  updateAIReviewJobName,
  getAIReviewDocErrorPanelList,
  updateProtocolIntelligenceDetails,
  getJobList,
  getGroundTruthEvaluation,
  getGroundTruthTags,
  deleteAIProtocolIntelligence,
  getAIReviewJobDetailData,
  getMasterDocIdErrorDoc,
  getAllProtocolIntelligenceList,
  generateProtocolArtifacts,
  createNewProtocol,
  saveProtocolAsNew,
  createProtocolIntelligence,
  validateProtocolName,
  updateProtocolName,
  getMasterDocIdForRelevancy,
  getAIReviewJobCloneHistory,
  getMasterDocIdForIssueCoding,
  getDocListMasterIdForJobDocs,
  updateProtocolActiveState,
  getProtocolNames,
  getProtocolVersionDetails,
  getProtocolVersions,
  updateProtocolVersionDetails,
  getUploadedDataById,
  uploadProtocolV2,
  getProtocolVersionHistory,
  createProtocolVersion,
} from '../../../api/endpoints/aiReviewApi';
import {
  IAIReviewJobGroupsResponse,
  ICreateAIReviewJobGroupPayload,
  ICreateAIReviewJobGroupResponse,
  IDeleteAIReviewJobGroupPayload,
  IUpdateAIReviewJobGroupPayload,
  IUpdateAIReviewJobGroupResponse,
  IAIReviewProtocolIntelligencePayload,
  IDeleteAIReviewJobGroupResponse,
  IGetAIReviewSourceTypeResponse,
  ICreateAIReviewJobResponse,
  ICreateAIReviewJobPayload,
  ISubmitAIReviewJobResponse,
  ISubmitAIReviewJobPayload,
  IGetAIReviewJobDetailsPayload,
  IGetAIReviewJobDetailsResponse,
  IUpdateAIReviewJobResponse,
  IUpdateAIReviewJobPayload,
  IAIReviewJobsExportRequest,
  IAIReviewJobsExportResponse,
  IExportAIReviewJobPdfPayload,
  IGetAIReviewJobResultPayload,
  IGetAIReviewJobResultResponse,
  IDeleteAIReviewJobPayload,
  IDeleteAIReviewJobResponse,
  IRetryAIReviewJobPayload,
  IRetryAIReviewJobResponse,
  IAddJobToGroupPayload,
  IAddJobToGroupResponse,
  IRemoveJobFromGroupPayload,
  IRemoveJobFromGroupResponse,
  IAIReviewJobResultDocsListPayload,
  IAIReviewJobResultDocsListResponse,
  IAIReviewJobResultRelevancyListPayload,
  IAIReviewJobResultRelevancyListResponse,
  IAIReviewRelevancyDocSummaryRequest,
  IAIReviewRelevancyDocSummary,
  IAIReviewEditJobNameResponse,
  IAIReviewEditJobNamePayload,
  IGetAIReviewDocErrorPanelPayload,
  IAIReviewDocErrorPanelResponse,
  IUpdateProtocolIntelligenceDetailsPayload,
  IGetJobListResponse,
  IGetJobListPayload,
  IGetGroundTruthEvaluationResponse,
  IGetGroundTruthTagsResponse,
  IDeleteAIProtocolIntelligenceResponse,
  IDeleteAIProtocolIntelligencePayload,
  ProtocolVersionsResponse,
  ProtocolIntelligenceListRequest,
  GenerateProtocolArtifactsPayload,
  GenerateProtocolArtifactsResponse,
  ProtocolDetailsResponse,
  IGetMasterDocIdForRelevancyPayload,
  IGetAIReviewCloneHistoryResponse,
  IGetMasterDocIdForIssueCodingResponse,
  IGetMasterDocIdForIssueCodingPayload,
  AIReviewCreateProtocolIntelligencePayload,
  ProtocolIntelligenceFileUploadResponse,
  IGetMasterDocIdForRelevancyAndDocResponse,
  IGetDocListMasterIdJobDocsPayload,
  ICreateNewProtocolPayloadProps,
  ICreateNewProtocolResponse,
  ISaveProtocolAsNewPayload,
  ISaveProtocolAsNewResponse,
  ICreateProtocolIntelligencePayload,
  ICreateProtocolIntelligenceResponse,
  ICreateProtocolVersionPayload,
  ICreateProtocolVersionResponse,
  IValidateProtocolNameRequestPayload,
  IValidateProtocolNameResponse,
  IUpdateProtocolNamePayload,
  IUpdateProtocolNameResponse,
  UpdateProtocolActiveStatePayload,
  ProtocolVersionDetailsResponseV2,
  UpdateProtocolVersionDetailsPayloadV2,
  UpdateProtocolVersionDetailsResponseV2,
  GetProtocolVersionsResponse,
  GetProtocolNamesResponse,
  IUploadProtocolPayload,
  IUploadProtocolResponse,
  ProtocolVersionHistoryResponse,
  FileUploadResponse,
  IDownloadAIReviewJobResultsPayload,
  IAIReviewJobResultIssueCodePayload,
  IAIReviewJobResultIssueCodeResponse,
} from '../../../api/types/aiReview.type';

export const REVIEW_JOB_GROUPS_QUERY_KEY = ['getAIReviewJobGroups'] as const;

export const PROTOCOL_INTELLIGENCE_QUERY_KEY = [
  'getProtocolIntelligence',
] as const;

export const PROTOCOL_INTELLIGENCE_LIST_QUERY_KEY = [
  'getProtocolIntelligenceList',
] as const;

export const PROTOCOL_VERSION_DETAILS_QUERY_KEY = [
  'getProtocolVersionDetails',
] as const;
export const PROTOCOL_VERSION_HISTORY_QUERY_KEY = [
  'getProtocolVersionHistory',
] as const;

export const PROTOCOL_VERSIONS_QUERY_KEY = ['getProtocolVersions'] as const;

export const PROTOCOL_NAMES_QUERY_KEY = ['getProtocolNames'] as const;

export const SOURCE_TYPE_QUERY_KEY = ['getSourceType'] as const;

export const REVIEW_JOB_QUERY_KEY = ['getAIReviewJob'] as const;

export const REVIEW_DOC_ERROR_PANEL_QUERY_KEY = [
  'getAIReviewDocErrorPanel',
] as const;

export const REVIEW_JOB_CLONE_HISTORY_QUERY_KEY = [
  'getAIReviewJobCloneHistory',
] as const;
export const GROUND_TRUTH_TAGS_QUERY_KEY = ['getGroundTruthTags'] as const;
export const RELEVANCY_DOC_SUMMARY_QUERY_KEY = [
  'getAIReviewRelevancyDocSummary',
] as const;

const isValidReviewIdentifier = (
  value: number | string | null | undefined
): value is number | string => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0;
  }

  if (typeof value !== 'string') {
    return false;
  }

  const normalized = value.trim();
  return /^\d+$/.test(normalized) && Number(normalized) > 0;
};

export const useGetReviewJobGroups = () => {
  return useQuery<IAIReviewJobGroupsResponse, Error>({
    queryKey: [...REVIEW_JOB_GROUPS_QUERY_KEY],
    queryFn: () => getAIReviewJobGroups(),
  });
};

export const useCreateReviewJobGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ICreateAIReviewJobGroupResponse,
    Error,
    ICreateAIReviewJobGroupPayload
  >({
    mutationFn: createAIReviewJobGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateReviewJobGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IUpdateAIReviewJobGroupResponse,
    Error,
    IUpdateAIReviewJobGroupPayload
  >({
    mutationFn: updateAIReviewJobGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteReviewJobGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IDeleteAIReviewJobGroupResponse,
    Error,
    IDeleteAIReviewJobGroupPayload
  >({
    mutationFn: deleteAIReviewJobGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

// ==================Protocol-V1=======================

export const useGetAllProtocolIntelligenceList = (
  params: ProtocolIntelligenceListRequest | string = '',
  enabled: boolean = true
) => {
  return useQuery<ProtocolVersionsResponse, Error>({
    queryKey: [...PROTOCOL_INTELLIGENCE_LIST_QUERY_KEY, params],
    queryFn: () => getAllProtocolIntelligenceList(params),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: false,
  });
};

export const useUpdateProtocolActiveState = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, UpdateProtocolActiveStatePayload>({
    mutationFn: updateProtocolActiveState,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROTOCOL_NAMES_QUERY_KEY,
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGenerateProtocolArtifacts = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    GenerateProtocolArtifactsResponse,
    Error,
    GenerateProtocolArtifactsPayload
  >({
    mutationFn: generateProtocolArtifacts,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetProtocolIntelligenceDetails = (
  protocolId?: number | null
) => {
  return useQuery<ProtocolDetailsResponse, Error>({
    queryKey: [...PROTOCOL_INTELLIGENCE_QUERY_KEY, protocolId],
    queryFn: () =>
      getProtocolIntelligenceDetails({
        protocolId: protocolId!,
      }),
    enabled: !!protocolId,
  });
};

export const useGetProtocolVersionHistory = (
  protocolId?: number | null,
  isPanelOpen: boolean = true
) => {
  return useQuery<ProtocolVersionHistoryResponse, Error>({
    queryKey: [...PROTOCOL_VERSION_HISTORY_QUERY_KEY, protocolId],
    queryFn: () => getProtocolVersionHistory({protocolId: protocolId!}),
    enabled: isPanelOpen && typeof protocolId === 'number' && protocolId > 0,
    retry: false,
  });
};

export const useGetSourceType = () => {
  return useQuery<IGetAIReviewSourceTypeResponse, Error>({
    queryKey: [...SOURCE_TYPE_QUERY_KEY],
    queryFn: () => getAIReviewSourceType(),
  });
};

export const useCreateAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ICreateAIReviewJobResponse,
    Error,
    ICreateAIReviewJobPayload
  >({
    mutationFn: createAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUploadProtocolIntelligence = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ProtocolIntelligenceFileUploadResponse,
    Error,
    AIReviewCreateProtocolIntelligencePayload
  >({
    mutationFn: uploadProtocolIntelligence,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateProtocolIntelligenceDetails = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, IUpdateProtocolIntelligenceDetailsPayload>({
    mutationFn: updateProtocolIntelligenceDetails,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetProtocolVersionDetails = (
  versionId?: number | null,
  enabled: boolean = true
) => {
  return useQuery<ProtocolVersionDetailsResponseV2, Error>({
    queryKey: [...PROTOCOL_VERSION_DETAILS_QUERY_KEY, versionId],
    queryFn: () =>
      getProtocolVersionDetails({
        versionId: versionId!,
      }),
    enabled: enabled && !!versionId,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useGetProtocolVersions = (
  protocolId?: number | null,
  enabled: boolean = true
) => {
  return useQuery<GetProtocolVersionsResponse, Error>({
    queryKey: [...PROTOCOL_VERSIONS_QUERY_KEY, protocolId],
    queryFn: () =>
      getProtocolVersions({
        protocolId: protocolId!,
      }),
    enabled: enabled && !!protocolId,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useGetProtocolNames = (enabled: boolean = true) => {
  return useQuery<GetProtocolNamesResponse, Error>({
    queryKey: [...PROTOCOL_NAMES_QUERY_KEY],
    queryFn: getProtocolNames,
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: false,
  });
};

export const useUpdateProtocolVersionDetails = (
  onSuccess?: (data: UpdateProtocolVersionDetailsResponseV2) => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    UpdateProtocolVersionDetailsResponseV2,
    Error,
    UpdateProtocolVersionDetailsPayloadV2
  >({
    mutationFn: updateProtocolVersionDetails,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ProtocolVersionDetailsResponseV2>(
        [...PROTOCOL_VERSION_DETAILS_QUERY_KEY, variables.versionId],
        (current) => {
          if (!current) {
            return current;
          }
          return {
            ...current,
            ...(variables.protocolName != null
              ? {protocolName: variables.protocolName}
              : {}),
            ...(variables.status != null ? {status: variables.status} : {}),
            ...(variables.caseContext
              ? {
                  caseContext: {
                    ...current.caseContext,
                    ...variables.caseContext,
                  },
                }
              : {}),
            ...(variables.relevanceCriteria
              ? {
                  relevanceCriteria: {
                    ...current.relevanceCriteria,
                    ...variables.relevanceCriteria,
                  },
                }
              : {}),
            ...(variables.issueCoding
              ? {
                  issueCoding: {
                    ...current.issueCoding,
                    ...variables.issueCoding,
                  },
                }
              : {}),
            ...(variables.fileDetails
              ? {fileDetails: variables.fileDetails}
              : {}),
          };
        }
      );
      queryClient.invalidateQueries({
        queryKey: [...PROTOCOL_VERSION_DETAILS_QUERY_KEY, variables.versionId],
      });
      queryClient.invalidateQueries({
        queryKey: [...PROTOCOL_VERSIONS_QUERY_KEY, data.protocolId],
      });
      queryClient.invalidateQueries({
        queryKey: PROTOCOL_INTELLIGENCE_LIST_QUERY_KEY,
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();

  return useMutation<
    IUpdateAIReviewJobResponse,
    Error,
    IUpdateAIReviewJobPayload
  >({
    mutationFn: updateAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});

      onSuccess?.();
    },
    onError: (err) => {
      onError?.(err);
    },
  });
};

export const useUpdateAIReviewJobName = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IAIReviewEditJobNameResponse,
    Error,
    IAIReviewEditJobNamePayload
  >({
    mutationFn: updateAIReviewJobName,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useApproveProtocolIntelligence = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, IAIReviewProtocolIntelligencePayload>({
    mutationFn: approveProtocolIntelligence,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSubmitAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ISubmitAIReviewJobResponse,
    Error,
    ISubmitAIReviewJobPayload
  >({
    mutationFn: submitAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAIReviewJobDetails = (
  payload: IGetAIReviewJobDetailsPayload,
  enabled: boolean
) => {
  return useQuery<IGetAIReviewJobDetailsResponse, Error>({
    queryKey: [...REVIEW_JOB_QUERY_KEY, payload],
    queryFn: () => getAIReviewJobDetails(payload),
    enabled,
    retry: false,
  });
};

export const useExportAIReviewJobs = () => {
  return useMutation<
    IAIReviewJobsExportResponse,
    Error,
    IAIReviewJobsExportRequest
  >({
    mutationFn: exportAIReviewJobs,
  });
};

export const useDownloadAIReviewJobResults = () => {
  return useMutation<string, Error, IDownloadAIReviewJobResultsPayload>({
    mutationFn: downloadAIReviewJobResults,
  });
};

export const useGetAIReviewJobDetailData = () => {
  return useMutation<
    IGetAIReviewJobDetailsResponse,
    Error,
    IExportAIReviewJobPdfPayload
  >({
    mutationFn: getAIReviewJobDetailData,
  });
};

export const useGetAIReviewJobResultForExport = () => {
  return useMutation<
    IGetAIReviewJobResultResponse,
    Error,
    IGetAIReviewJobResultPayload
  >({
    mutationFn: getAIReviewJobResultForExport,
  });
};

export const useDeleteAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IDeleteAIReviewJobResponse,
    Error,
    IDeleteAIReviewJobPayload
  >({
    mutationFn: deleteAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRetryAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IRetryAIReviewJobResponse,
    Error,
    IRetryAIReviewJobPayload
  >({
    mutationFn: retryAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useAddJobToGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<IAddJobToGroupResponse, Error, IAddJobToGroupPayload>({
    mutationFn: addJobToGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRemoveJobFromGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IRemoveJobFromGroupResponse,
    Error,
    IRemoveJobFromGroupPayload
  >({
    mutationFn: removeJobFromGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAIReviewJobResult = (
  payload: IGetAIReviewJobResultPayload
) => {
  return useQuery<IGetAIReviewJobResultResponse, Error>({
    queryKey: [...REVIEW_JOB_QUERY_KEY, 'result', payload],
    queryFn: () => getAIReviewJobResult(payload),
  });
};

export const useGetAIReviewJobResultIssueCodeList = (
  jobId: number | string,
  payload: IAIReviewJobResultIssueCodePayload,
  enabled: boolean = true
) => {
  return useQuery<IAIReviewJobResultIssueCodeResponse, Error>({
    queryKey: [...REVIEW_JOB_QUERY_KEY, jobId, 'issue-code-list', payload],
    queryFn: () => getAIReviewJobResultIssueCodeList(jobId, payload),
    enabled: enabled && jobId != null && jobId !== '',
  });
};

export const useGetAIReviewJobResultDocsList = (
  jobId: number | string,
  payload: IAIReviewJobResultDocsListPayload,
  enabled: boolean = true
) => {
  return useQuery<IAIReviewJobResultDocsListResponse, Error>({
    queryKey: [...REVIEW_JOB_QUERY_KEY, jobId, 'docs-list', payload],
    queryFn: () => getAIReviewJobResultDocsList(jobId, payload),
    enabled: enabled && jobId != null && jobId !== '',
  });
};

export const useGetAIReviewJobResultRelevancyList = (
  jobId: number | string,
  payload: IAIReviewJobResultRelevancyListPayload,
  enabled: boolean = true
) => {
  return useQuery<IAIReviewJobResultRelevancyListResponse, Error>({
    queryKey: [...REVIEW_JOB_QUERY_KEY, jobId, 'relevancy-list', payload],
    queryFn: () => getAIReviewJobResultRelevancyList(jobId, payload),
    enabled: enabled && jobId != null && jobId !== '',
  });
};

export const useGetAIReviewRelevancyDocSummary = (
  payload: IAIReviewRelevancyDocSummaryRequest,
  enabled: boolean = true
) => {
  const hasValidIdentifiers =
    isValidReviewIdentifier(payload?.reviewjobid) &&
    isValidReviewIdentifier(payload?.reviewJobSubmittedId);

  return useQuery<IAIReviewRelevancyDocSummary, Error>({
    queryKey: [
      ...RELEVANCY_DOC_SUMMARY_QUERY_KEY,
      payload?.reviewjobid,
      payload?.reviewJobSubmittedId,
    ],
    queryFn: () => getAIReviewRelevancyDocSummary(payload),
    enabled: enabled && hasValidIdentifiers,
    retry: false,
  });
};

export const useGetAIReviewDocErrorPanel = (
  payload: IGetAIReviewDocErrorPanelPayload
) => {
  return useQuery<IAIReviewDocErrorPanelResponse, Error>({
    queryKey: [...REVIEW_DOC_ERROR_PANEL_QUERY_KEY, 'result', payload],
    queryFn: () => getAIReviewDocErrorPanelList(payload),
    enabled: !!payload?.page,
  });
};

export const useGetJobList = (
  onSuccess?: (response: IGetJobListResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IGetJobListResponse, Error, IGetJobListPayload>({
    mutationFn: (payload) => getJobList(payload),
    mutationKey: ['getJobList'],
    onSuccess: (response: IGetJobListResponse) => {
      onSuccess?.(response);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetGroundTruthEvaluation = (jobId: string) => {
  return useQuery<IGetGroundTruthEvaluationResponse, Error>({
    queryKey: ['getGroundTruthEvaluation', jobId],
    queryFn: () => getGroundTruthEvaluation(jobId),
    enabled: Boolean(jobId),
  });
};

export const useGetGroundTruthTags = (enabled: boolean = true) => {
  return useQuery<IGetGroundTruthTagsResponse, Error>({
    queryKey: [...GROUND_TRUTH_TAGS_QUERY_KEY],
    queryFn: () => getGroundTruthTags(),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    retry: false,
  });
};

export const useDeleteAIProtocolIntelligence = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IDeleteAIProtocolIntelligenceResponse,
    Error,
    IDeleteAIProtocolIntelligencePayload
  >({
    mutationFn: deleteAIProtocolIntelligence,
    onSuccess: () => {
      onSuccess?.();
    },
    onSettled: async (_data, error, variables) => {
      if (error) {
        return;
      }

      // Refresh list only — do not touch the details query (avoids refetch by id)
      await queryClient.invalidateQueries({
        queryKey: PROTOCOL_INTELLIGENCE_LIST_QUERY_KEY,
      });

      queryClient.removeQueries({
        queryKey: [...PROTOCOL_INTELLIGENCE_QUERY_KEY, variables.protocolId],
        type: 'inactive',
      });
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetMasterDocIdErrorDoc = (
  onSuccess?: (response: IGetMasterDocIdForRelevancyAndDocResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IGetMasterDocIdForRelevancyAndDocResponse,
    Error,
    IGetMasterDocIdForRelevancyPayload
  >({
    mutationFn: (payload) => getMasterDocIdErrorDoc(payload),
    mutationKey: ['getMasterDocIdErrorDoc'],
    onSuccess: (response: IGetMasterDocIdForRelevancyAndDocResponse) => {
      onSuccess?.(response);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCreateNewProtocol = (
  onSuccess?: (data: ICreateNewProtocolResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ICreateNewProtocolResponse,
    Error,
    ICreateNewProtocolPayloadProps
  >({
    mutationFn: createNewProtocol,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSaveProtocolAsNew = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ISaveProtocolAsNewResponse,
    Error,
    ISaveProtocolAsNewPayload
  >({
    mutationFn: saveProtocolAsNew,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/** AVR-21735: Create protocol V2 with full form sections + status. */
export const useCreateProtocolIntelligence = (
  onSuccess?: (data: ICreateProtocolIntelligenceResponse) => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ICreateProtocolIntelligenceResponse,
    Error,
    ICreateProtocolIntelligencePayload
  >({
    mutationFn: createProtocolIntelligence,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: PROTOCOL_INTELLIGENCE_LIST_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: [...PROTOCOL_VERSIONS_QUERY_KEY, data.protocolId],
      });
      queryClient.invalidateQueries({
        queryKey: [...PROTOCOL_VERSION_DETAILS_QUERY_KEY, data.versionId],
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/** AVR-21867: Create a new protocol version under an existing protocol. */
export const useCreateProtocolVersion = (
  onSuccess?: (data: ICreateProtocolVersionResponse) => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ICreateProtocolVersionResponse,
    Error,
    ICreateProtocolVersionPayload
  >({
    mutationFn: createProtocolVersion,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...PROTOCOL_VERSIONS_QUERY_KEY, variables.protocolId],
      });
      queryClient.invalidateQueries({
        queryKey: [...PROTOCOL_VERSION_DETAILS_QUERY_KEY, data.versionId],
      });
      queryClient.invalidateQueries({
        queryKey: PROTOCOL_INTELLIGENCE_LIST_QUERY_KEY,
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useValidateProtocolName = (
  onSuccess?: (data: IValidateProtocolNameResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IValidateProtocolNameResponse,
    Error,
    IValidateProtocolNameRequestPayload
  >({
    mutationFn: validateProtocolName,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateProtocolName = (
  onSuccess?: (data: IUpdateProtocolNameResponse) => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IUpdateProtocolNameResponse,
    Error,
    IUpdateProtocolNamePayload
  >({
    mutationFn: updateProtocolName,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: PROTOCOL_INTELLIGENCE_LIST_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: PROTOCOL_NAMES_QUERY_KEY,
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetMasterDocIdForRelevancy = (
  onSuccess?: (response: IGetMasterDocIdForRelevancyAndDocResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IGetMasterDocIdForRelevancyAndDocResponse,
    Error,
    IGetMasterDocIdForRelevancyPayload
  >({
    mutationFn: (payload) => getMasterDocIdForRelevancy(payload),
    mutationKey: ['getMasterDocIdForRelevancy'],
    onSuccess: (response: IGetMasterDocIdForRelevancyAndDocResponse) => {
      onSuccess?.(response);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetMasterDocIdForIssueCoding = (
  onSuccess?: (response: IGetMasterDocIdForRelevancyAndDocResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IGetMasterDocIdForIssueCodingResponse,
    Error,
    IGetMasterDocIdForIssueCodingPayload
  >({
    mutationFn: (payload) => getMasterDocIdForIssueCoding(payload),
    mutationKey: ['getMasterDocIdForIssueCoding'],
    onSuccess: (response: IGetMasterDocIdForRelevancyAndDocResponse) => {
      onSuccess?.(response);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAIReviewJobCloneHistory = (
  jobId?: string | number,
  enabled: boolean = true
) => {
  return useQuery<IGetAIReviewCloneHistoryResponse, Error>({
    queryKey: [...REVIEW_JOB_CLONE_HISTORY_QUERY_KEY, jobId],
    queryFn: () => getAIReviewJobCloneHistory(jobId!),
    enabled: enabled && jobId != null,
    retry: false,
  });
};
export const useGetDocListMasterIdJobDocs = (
  payload: IGetDocListMasterIdJobDocsPayload
) => {
  return useQuery<any, Error, any>({
    queryKey: [
      'GetDocListMasterIdForJobDocs',
      payload?.reviewJobId,
      payload?.type,
    ],
    queryFn: () => getDocListMasterIdForJobDocs(payload),
    enabled: !!payload?.reviewJobId,
  });
};

//v2 hooks
export const useUploadProtocolFileV2 = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<FileUploadResponse, Error, IUploadProtocolPayload>({
    mutationFn: uploadProtocolV2,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetUploadedDataById = (artifactId?: number | null) => {
  return useQuery<IUploadProtocolResponse, Error>({
    queryKey: ['getProtocolIntelligenceV2', artifactId],
    queryFn: () =>
      getUploadedDataById({
        artifactId: artifactId!,
      }),
    enabled: !!artifactId,
  });
};
